import { CloseOutlined, ContainerOutlined } from '@ant-design/icons';
import { Form, Input, InputNumber, Select, Switch, Tooltip, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FormSection, SectionRepeatablePayload, WorkflowStep } from '@/api/workflows';

const { Text } = Typography;

// Width and left border live in `.fb-properties` (global.css) — below 1024px
// this panel is a full-screen sheet and keeps neither. Same rail width as the
// Field properties panel's compact state, which is that class's default.
const RAIL_BASE: CSSProperties = {
  flexShrink: 0,
  background: '#fff',
  padding: 18,
  overflowY: 'auto',
};
const COMPACT_ITEM: CSSProperties = { marginBottom: 10 };

// Defaults applied the first time an originator flips Repeatable on. Kept in
// sync with the backend's coherence rules (min >= 1, max >= min).
const DEFAULT_MIN_INSTANCES = 1;
const DEFAULT_MAX_INSTANCES = 3;
/**
 * "Joint on the second instance" — the only reading the joint tag had before the
 * threshold was authorable, and so the value that keeps every existing section
 * behaving exactly as it does today. Mirrors
 * `FormSection.DefaultJointApplicationThreshold` on the backend.
 */
const DEFAULT_JOINT_THRESHOLD = 1;

type SaveDetailsPayload = { titleEn: string; titleFr: string | null; hideEditInReview: boolean };
type Props = {
  /** The section being edited; null hides the panel (nothing selected). */
  section: FormSection | null;
  /** Every step of the workflow — the "Repeat once per instance of" picker lists the
   *  repeatable sections on any of them. */
  allSteps: WorkflowStep[];
  /** Persist the bilingual section name (PATCH /sections/{id}). */
  onSaveDetails: (payload: SaveDetailsPayload) => Promise<void>;
  /** Persist the repeatable toggle + min/max bounds (PUT /sections/{id}/repeatable). */
  onSaveRepeatable: (payload: SectionRepeatablePayload) => Promise<void>;
  onClose: () => void;
};

type FormValues = {
  titleEn: string;
  titleFr: string;
  repeatable: boolean;
  minInstances: number;
  maxInstances: number | null;
  hideEditInReview: boolean;
  /** Select value; undefined = not mirroring. */
  repeatWithSectionId?: string;
  /** Repeatable only — this section's instance count feeds appInfo.jointApplication. */
  jointApplicationSelector: boolean;
  /** The instance count the tag must EXCEED to read as joint. See DEFAULT_JOINT_THRESHOLD. */
  jointApplicationThreshold: number;
};

/**
 * Right-rail editor for a selected form section — the section-level sibling of
 * {@link FieldPropertiesPanel}. Deliberately minimal for now: bilingual name +
 * a Repeatable toggle that reveals min/max instance bounds. Auto-saves on every
 * change (debounced) with the same three-state save-dot the field panel uses.
 *
 * Only rendered for the workflow-backed editor (the Custom Editor has no
 * repeatable authoring); the shell passes handlers that route through
 * `dataSource.editSectionDetails` / `dataSource.setSectionRepeatable`.
 */
export function SectionPropertiesPanel({
  section,
  allSteps,
  onSaveDetails,
  onSaveRepeatable,
  onClose,
}: Props) {
  // Mounted only when a section is selected; caller owns dismissal via onClose.
  if (!section) return null;
  // Key on section.id so switching sections gets a fresh form instance (no
  // stale-values race when AntD reuses the form between mounts) — same guard
  // FieldPropertiesPanel uses for fields.
  return (
    <SectionEditor
      key={section.id}
      section={section}
      allSteps={allSteps}
      onSaveDetails={onSaveDetails}
      onSaveRepeatable={onSaveRepeatable}
      onClose={onClose}
    />
  );
}

function SectionEditor({
  section,
  allSteps,
  onSaveDetails,
  onSaveRepeatable,
  onClose,
}: {
  section: FormSection;
  allSteps: WorkflowStep[];
  onSaveDetails: Props['onSaveDetails'];
  onSaveRepeatable: Props['onSaveRepeatable'];
  onClose: Props['onClose'];
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  // Reactive watches so the min/max inputs reveal in lockstep with the toggle
  // and the max floor tracks the current min.
  const repeatable = Form.useWatch<boolean | undefined>('repeatable', form);
  const minInstances = Form.useWatch<number | undefined>('minInstances', form);
  // The threshold only means anything alongside the tag, so the input is revealed
  // by the switch rather than sitting there inert next to an untagged section.
  const jointTagged = Form.useWatch<boolean | undefined>('jointApplicationSelector', form);
  const maxInstances = Form.useWatch<number | null | undefined>('maxInstances', form);

  // Captured once at mount (keyed on section.id so switching sections remounts).
  // For an already-repeatable section reflect its true stored bounds (a null
  // max stays empty = unlimited so we never silently cap it on the next edit).
  // For a not-yet-repeatable section seed the "turn it on" defaults (1 / 3) so
  // flipping Repeatable reveals them rather than empty inputs.
  const [initialValues] = useState<FormValues>(() => {
    const isRepeatable = section.repeatable ?? false;
    return {
      titleEn: section.titleEn,
      titleFr: section.titleFr,
      repeatable: isRepeatable,
      minInstances: isRepeatable
        ? (section.minInstances ?? DEFAULT_MIN_INSTANCES)
        : DEFAULT_MIN_INSTANCES,
      maxInstances: isRepeatable ? (section.maxInstances ?? null) : DEFAULT_MAX_INSTANCES,
      hideEditInReview: section.hideEditInReview ?? false,
      repeatWithSectionId: section.repeatWithSectionId ?? undefined,
      // Only meaningful while repeatable, and the BE drops it when repeatability
      // is switched off — so a non-repeatable section always seeds false rather
      // than reflecting a stale stored true.
      jointApplicationSelector: isRepeatable ? (section.jointApplicationSelector ?? false) : false,
      // Seeded from the default rather than left blank so an originator who ticks
      // the tag and saves immediately gets today's "more than one instance"
      // behaviour instead of a 0 that would report every filled section as joint.
      jointApplicationThreshold: section.jointApplicationThreshold ?? DEFAULT_JOINT_THRESHOLD,
    };
  });

  // "Repeat once per instance of" candidates: every OTHER repeatable section of the workflow
  // that mirrors nothing itself (the domain refuses chains). A section others already mirror
  // cannot start mirroring, so the picker is disabled for it and says why.
  const { repeatWithOptions, isMirrored } = useMemo(() => {
    const sections = allSteps.flatMap((st) =>
      (st.sections ?? []).map((sec) => ({ sec, stepTitle: st.titleEn || st.titleFr })),
    );
    return {
      repeatWithOptions: sections
        .filter(({ sec }) => sec.id !== section.id && sec.repeatable && !sec.repeatWithSectionId)
        .map(({ sec, stepTitle }) => ({
          value: sec.id,
          label: `${sec.titleEn || sec.titleFr || sec.id} — ${stepTitle}`,
        })),
      isMirrored: sections.some(({ sec }) => sec.repeatWithSectionId === section.id),
    };
  }, [allSteps, section.id]);

  type AutoSaveStatus = 'saved' | 'saving' | 'error';
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('saved');
  const [autoSaveErrorText, setAutoSaveErrorText] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Counts in-flight requests so a stale response can't mask a newer failure.
  const inFlightCountRef = useRef(0);

  const persistNow = useCallback(async () => {
    inFlightCountRef.current += 1;
    setAutoSaveStatus('saving');
    try {
      const values = await form.validateFields();
      const isRepeatable = values.repeatable ?? false;
      await onSaveDetails({
        titleEn: values.titleEn,
        titleFr: values.titleFr ? values.titleFr : null,
        hideEditInReview: values.hideEditInReview ?? false,
      });
      // Collapse the bounds to their coherent shape when the section is not
      // repeatable — the backend refuses a non-null MaxInstances without
      // Repeatable = true, so never send stale bounds from the hidden inputs.
      // The PUT is whole-state, so the instance label (authored outside this panel) is sent
      // back as stored — omitting it would erase "Accountholder {n}" on every save here.
      await onSaveRepeatable({
        repeatable: isRepeatable,
        minInstances: isRepeatable ? (values.minInstances ?? DEFAULT_MIN_INSTANCES) : 1,
        maxInstances: isRepeatable ? (values.maxInstances ?? null) : null,
        instanceLabelEn: isRepeatable ? (section.instanceLabelEn ?? null) : null,
        instanceLabelFr: isRepeatable ? (section.instanceLabelFr ?? null) : null,
        repeatWithSectionId: isRepeatable ? (values.repeatWithSectionId ?? null) : null,
        // Same collapse as the bounds: the BE refuses the tag without
        // Repeatable, so never send a stale true from the hidden switch.
        jointApplicationSelector: isRepeatable ? (values.jointApplicationSelector ?? false) : false,
        // Only meaningful alongside the tag; the BE normalizes it back to the
        // default whenever the tag is off, so send the default rather than a
        // stale number from the hidden input.
        jointApplicationThreshold:
          isRepeatable && values.jointApplicationSelector
            ? (values.jointApplicationThreshold ?? DEFAULT_JOINT_THRESHOLD)
            : DEFAULT_JOINT_THRESHOLD,
      });
      inFlightCountRef.current -= 1;
      if (inFlightCountRef.current === 0) {
        setAutoSaveStatus('saved');
        setAutoSaveErrorText(null);
      }
    } catch (err) {
      inFlightCountRef.current -= 1;
      const msg = err instanceof Error ? err.message : String(err);
      setAutoSaveErrorText(msg);
      setAutoSaveStatus('error');
    }
  }, [form, onSaveDetails, onSaveRepeatable, section.instanceLabelEn, section.instanceLabelFr]);

  // Debounce 500ms so rapid typing produces one round-trip, not N. Matches the
  // field panel's cadence.
  const handleValuesChange = () => {
    setAutoSaveStatus('saving');
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void persistNow();
    }, 500);
  };

  // Flush a pending save if the panel closes mid-debounce, and clear the timer
  // on unmount so a stale callback can't fire after the panel is gone.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        void persistNow();
      }
    };
  }, [persistNow]);

  const statusLabel =
    autoSaveStatus === 'saved'
      ? t('workflow.builder.properties.autoSaveSaved')
      : autoSaveStatus === 'saving'
        ? t('workflow.builder.properties.autoSaveSaving')
        : t('workflow.builder.properties.autoSaveError', { error: autoSaveErrorText ?? '' });

  return (
    <aside className="fb-properties" style={RAIL_BASE}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          paddingBottom: 14,
          marginBottom: 16,
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: '#eef2ff',
            color: '#4f46e5',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
          }}
        >
          <ContainerOutlined />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 14, lineHeight: 1.2, display: 'block' }}>
            {t('workflow.builder.sectionProperties.title')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>
            {t('workflow.builder.sectionProperties.subtitle')}
          </Text>
        </div>
        <Tooltip title={statusLabel}>
          <span
            role="status"
            aria-label={statusLabel}
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: 999,
              background:
                autoSaveStatus === 'saved'
                  ? '#16a34a'
                  : autoSaveStatus === 'saving'
                    ? '#ad6800'
                    : '#dc2626',
              transition: 'background-color 140ms ease',
              flexShrink: 0,
            }}
          />
        </Tooltip>
        <Tooltip title={t('workflow.builder.properties.close')}>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('workflow.builder.properties.close')}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#6b7280',
              display: 'inline-flex',
              alignItems: 'center',
              padding: 4,
            }}
          >
            <CloseOutlined />
          </button>
        </Tooltip>
      </div>

      <style>{`
        .builder-section-props-form .ant-form-item-label { padding-bottom: 2px; }
        .builder-section-props-form .ant-form-item-label > label { height: auto; }
      `}</style>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
        className="builder-section-props-form"
      >
        <Form.Item
          label={t('workflow.builder.sectionProperties.nameEn')}
          name="titleEn"
          style={COMPACT_ITEM}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={t('workflow.builder.sectionProperties.nameFr')}
          name="titleFr"
          style={COMPACT_ITEM}
        >
          <Input />
        </Form.Item>

        <div
          style={{
            marginTop: 8,
            paddingTop: 14,
            borderTop: '1px dashed #e5e7eb',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Form.Item name="repeatable" valuePropName="checked" noStyle>
              {/* Labelled like its two siblings below — a `noStyle` Form.Item gives the
                  control no accessible name of its own, so the heading beside it is
                  invisible to a screen reader (and to a by-name query). */}
              <Switch aria-label={t('workflow.builder.sectionProperties.repeatable')} />
            </Form.Item>
            <Text strong style={{ fontSize: 13 }}>
              {t('workflow.builder.sectionProperties.repeatable')}
            </Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            {t('workflow.builder.sectionProperties.repeatableHint')}
          </Text>

          {repeatable && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Item
                label={t('workflow.builder.sectionProperties.minInstances')}
                name="minInstances"
                style={COMPACT_ITEM}
                rules={[
                  {
                    required: true,
                    message: t('workflow.builder.sectionProperties.minRequired'),
                  },
                ]}
              >
                {/* 0 = optional block: nothing renders until the advisor
                    clicks Add. Pair with a max of 1 for the "add this once,
                    or not at all" pattern. */}
                <InputNumber min={0} max={100} style={{ width: '100%' }} controls={false} />
              </Form.Item>
              <Form.Item
                label={t('workflow.builder.sectionProperties.maxInstances')}
                name="maxInstances"
                style={COMPACT_ITEM}
                dependencies={['minInstances']}
                rules={[
                  {
                    validator: (_rule, value: number | null | undefined) => {
                      if (value === null || value === undefined) return Promise.resolve();
                      const min = minInstances ?? DEFAULT_MIN_INSTANCES;
                      if (value < min) {
                        return Promise.reject(
                          new Error(t('workflow.builder.sectionProperties.maxBelowMin')),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  // A cap of 0 would leave a section that can never hold an
                  // instance, so the cap floor is 1 even when the min is 0.
                  min={Math.max(1, minInstances ?? 1)}
                  max={100}
                  style={{ width: '100%' }}
                  controls={false}
                />
              </Form.Item>
            </div>
          )}
          {repeatable && (
            <>
              <Form.Item
                label={t('workflow.builder.sectionProperties.repeatWith')}
                name="repeatWithSectionId"
                style={COMPACT_ITEM}
              >
                <Select
                  allowClear
                  disabled={isMirrored}
                  placeholder={t('workflow.builder.sectionProperties.repeatWithNone')}
                  options={repeatWithOptions}
                  aria-label={t('workflow.builder.sectionProperties.repeatWith')}
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                {t(
                  isMirrored
                    ? 'workflow.builder.sectionProperties.repeatWithMirrored'
                    : 'workflow.builder.sectionProperties.repeatWithHint',
                )}
              </Text>

              {/* Reads the instance COUNT, not an answer — which is why it sits
                  here on the section rather than on some field inside it, and
                  why it only appears once the section can actually repeat. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Form.Item name="jointApplicationSelector" valuePropName="checked" noStyle>
                  <Switch
                    aria-label={t('workflow.builder.sectionProperties.jointApplicationSelector')}
                  />
                </Form.Item>
                <Text strong style={{ fontSize: 13 }}>
                  {t('workflow.builder.sectionProperties.jointApplicationSelector')}
                </Text>
              </div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {t('workflow.builder.sectionProperties.jointApplicationSelectorHint')}
              </Text>

              {/* "Joint when instances exceed N". 1 = a per-party section that already
                  holds the primary party (joint on the second). 0 = an optional block
                  holding ONLY the extra party (joint as soon as it exists) — the shape
                  the old fixed "more than one" could never express. */}
              {jointTagged && (
                <div style={{ marginTop: 10 }}>
                  <Form.Item
                    label={t('workflow.builder.sectionProperties.jointApplicationThreshold')}
                    name="jointApplicationThreshold"
                    style={COMPACT_ITEM}
                    rules={[
                      {
                        // A threshold at or above the cap can never be exceeded, so the
                        // section would report "not joint" however it is filled. Caught
                        // here as well as in the domain so the originator sees it while
                        // they are looking at both numbers.
                        validator: (_, value: number | null | undefined) => {
                          if (value === null || value === undefined) return Promise.resolve();
                          if (typeof maxInstances === 'number' && value >= maxInstances) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'workflow.builder.sectionProperties.jointApplicationThresholdTooHigh',
                                ),
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      step={1}
                      precision={0}
                      style={{ width: 90 }}
                      aria-label={t('workflow.builder.sectionProperties.jointApplicationThreshold')}
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    {t('workflow.builder.sectionProperties.jointApplicationThresholdHint')}
                  </Text>
                </div>
              )}
            </>
          )}
        </div>

        {/* Review-step presentation only: the section still recaps, just without the Edit
            link back to it (QA D-07a — RDSP's Eligibility gate must not be reopened there). */}
        <div
          style={{
            marginTop: 8,
            paddingTop: 14,
            borderTop: '1px dashed #e5e7eb',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Form.Item name="hideEditInReview" valuePropName="checked" noStyle>
              <Switch aria-label={t('workflow.builder.sectionProperties.hideEditInReview')} />
            </Form.Item>
            <Text strong style={{ fontSize: 13 }}>
              {t('workflow.builder.sectionProperties.hideEditInReview')}
            </Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            {t('workflow.builder.sectionProperties.hideEditInReviewHint')}
          </Text>
        </div>
      </Form>
    </aside>
  );
}
